import { Typo } from "@pepperstone/pepper-ui-kit";
import { InputField } from "@pepperstone/pepper-ui-kit/atoms/input-field/InputField";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Error as SdkError } from "@/api/cashier";
import { BaseModal } from "@/components/general/base-modal/BaseModal";
import { useUpdateCardExpiryDate } from "@/hooks/deposit/useUpdateCardExpiryDate";
import { useLogger } from "@/hooks/useLogger";
import style from "./UpdateCardExpiryDateModal.module.css";

type UpdateCardExpiryDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  savedPaymentAccountID: string;
  nameOnCard: string;
  cardNumber: string;
  expiryDate: string;
};

export const UpdateCardExpiryDateModal = ({
  isOpen,
  onClose,
  onSuccess,
  savedPaymentAccountID,
  nameOnCard,
  cardNumber,
  expiryDate,
}: UpdateCardExpiryDateModalProps) => {
  const { t } = useTranslation();
  const [expiry, setExpiry] = useState(expiryDate);
  const [error, setError] = useState<string | undefined>();
  const formRef = useRef<HTMLFormElement>(null);
  const logger = useLogger();

  useEffect(() => {
    if (isOpen) {
      setExpiry(expiryDate);
      setError(undefined);
    }
  }, [isOpen, expiryDate]);

  const onUpdateSucces = () => {
    onClose();
    onSuccess?.();
  };

  const onUpdateError = (err: SdkError) => {
    logger.error("Error updating card expiry date: ", err);
    setError(err?.message);
  };

  const { mutate: updateExpiryDate, isPending } = useUpdateCardExpiryDate(
    savedPaymentAccountID,
    onUpdateSucces,
    onUpdateError
  );

  const validateExpiry = (expiry: string): string | undefined => {
    if (!/^\d{2}\/\d{2}$/.test(expiry)) {
      return t("deposit.update_expiry_modal.errors.invalid_expiry");
    }

    const [monthStr, yearStr] = expiry.split("/");
    const month = Number(monthStr);
    const year = Number(yearStr);

    if (month < 1 || month > 12) {
      return t("deposit.update_expiry_modal.errors.invalid_expiry_month");
    }

    const now = new Date();
    const expiryDate = new Date(2000 + year, month, 0);

    if (expiryDate < now) {
      return t("deposit.update_expiry_modal.errors.expired_card");
    }

    return undefined;
  };

  const isExpiryFormatValid = /^\d{2}\/\d{2}$/.test(expiry);
  const isButtonDisabled = isPending || !isExpiryFormatValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errorMsg = validateExpiry(expiry);
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    setError(undefined);

    updateExpiryDate(expiry);
  };

  return (
    <BaseModal
      title={t("deposit.update_expiry_modal.title")}
      isOpen={isOpen}
      onClose={onClose}
      testId="update-card-expiry-modal"
      submitButtonContent={t("deposit.update_expiry_modal.save")}
      onSubmit={() => formRef.current?.requestSubmit()}
      isSubmitButtonDisabled={isButtonDisabled}
      isSubmitButtonLoading={isPending}
    >
      <form ref={formRef} onSubmit={handleSubmit} className={style.form}>
        <Typo elem="p" type="body" size="md">
          {t("deposit.update_expiry_modal.description")}
        </Typo>

        <InputField
          label={t("deposit.update_expiry_modal.name_on_card")}
          value={nameOnCard}
          disabled={true}
        />
        <InputField
          label={t("deposit.update_expiry_modal.card_number")}
          value={cardNumber}
          disabled={true}
        />
        <InputField
          label={t("deposit.update_expiry_modal.expiry_date")}
          name="expiryDate"
          placeholder="MM/YY"
          autoFocus={true}
          value={expiry}
          onChange={(e) => {
            setExpiry(e.target.value);
            setError(undefined);
          }}
          disabled={isPending}
          errorText={error}
        />
      </form>
    </BaseModal>
  );
};
